import { Box, styled, tooltipClasses } from '@mui/material';

import { CircularProgress, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChargeType } from '../../../data/models/charge-type.model';
import { financialUtils } from '../../../services/financialUtils';
import {
  TripLegResponseItem,
  TripLegAddOnLineItem,
} from '../../../data/models/trip-leg.model';
import { TripLegPricings } from '../../../data/models/trip-leg-pricings.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';

const PREFIX = 'PricingContent';

const priceContentClasses = {
  tooltipContent: `${PREFIX}-priceTooltipContent`,
  priceBreakdown: `${PREFIX}-priceBreakdown`,
  priceItem: `${PREFIX}-priceItem`,
  priceLabel: `${PREFIX}-priceLabel`,
  priceDetail: `${PREFIX}-priceDetail`,
  priceTotal: `${PREFIX}-priceTotal`,
};
const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    borderRadius: 3,
    background: '#FFF',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',
    color: 'rgba(1, 16, 18, 0.90)',
    padding: theme.spacing(1, 2),
    width: 308,
  },
  [`& .${priceContentClasses.tooltipContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  [`& .${priceContentClasses.priceBreakdown}.MuiTypography-root`]: {
    fontWeight: 600,
    padding: theme.spacing(0.25, 0),
  },
  [`& .${priceContentClasses.priceItem}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  [`& .MuiTypography-root`]: {
    fontSize: 14,
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .MuiTooltip-arrow`]: {
    color: '#fff',
  },
  [`& .${priceContentClasses.priceLabel}`]: {
    width: '122px',
  },
  [`& .${priceContentClasses.priceDetail}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
  },
  [`& .${priceContentClasses.priceTotal}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    '& .MuiTypography-root': {
      fontWeight: 600,
    },
  },
}));

export function PricingContent({
  tripLeg,
  pricings,
  chargeTypes,
  addOnLineItems,
}: {
  tripLeg: TripLegResponseItem;
  pricings: TripLegPricings | null;
  chargeTypes: ChargeType[];
  addOnLineItems?: TripLegAddOnLineItem[] | null;
}) {
  const { t } = useTranslation('common');
  if (!pricings) {
    return <CircularProgress />;
  }

  const getPricing = () => {
    if (!pricings) {
      return null;
    }
    // If there is a completed pricing use it. If not just use the first one
    // since there should be only one and it is the estimated price.
    const completed = pricings.provider.find(
      (p: any) => p.tripLegStatus === TripLegStatus.Completed,
    );

    const noLoadOrLateCancel = pricings.provider.find(
      (p: any) =>
        p.tripLegStatus === TripLegStatus.CanceledLate ||
        p.tripLegStatus === TripLegStatus.NoLoad,
    );
    return completed || noLoadOrLateCancel || pricings.provider[0];
  };
  const pricing = getPricing();

  const getChargeTypeName = (bc: string) => {
    const chargeType = chargeTypes.find((ct) => ct.chargeTypeBC === bc);
    return chargeType?.chargeTypeName || '';
  };

  const getDescription = (leg: any, chargeLine: any) => {
    let description = '';
    switch (chargeLine.pricingFunctionBC) {
      case 'FixedFee':
        description = financialUtils.formatPrice(
          Number(chargeLine.chargeLineAmount),
        );
        break;
      case 'PerMileRounded':
        description = t('needsAttention.perMile', {
          miles: leg?.invoiceDistanceMiles || leg?.estimatedDistanceMiles || '',
        });
        break;
      case 'WaitFee':
        description = t('needsAttention.waitMinutes', {
          minutes: Math.round(leg?.driverWaitTimeSeconds / 60),
        });
        break;
      case 'ClientPassthrough':
      case 'ProviderPassthrough':
        description = description = financialUtils.formatPrice(
          Number(chargeLine.chargeLineAmount),
        );
        break;
      default:
        // This should not happen but we can just display the pricing function so it's obvious to QA what is missing
        description = chargeLine.pricingFuntionBC;
        break;
    }
    return description;
  };

  const getLineItem = (chargeLine: any) => {
    return {
      name: getChargeTypeName(chargeLine.chargeTypeBC),
      description: getDescription(tripLeg, chargeLine),
      amount: financialUtils.formatPrice(Number(chargeLine.chargeLineAmount)),
    };
  };

  const calculateTotal = () => {
    const baseAmount = Number(pricing?.payableAmount) || 0;
    const addOnTotal =
      addOnLineItems?.reduce((sum, addOn) => {
        return sum + financialUtils.parsePrice(addOn.lineItemAmount);
      }, 0) || 0;
    return baseAmount + addOnTotal;
  };

  return (
    <StyledBox>
      <Box className={priceContentClasses.tooltipContent}>
        <Typography className={priceContentClasses.priceBreakdown}>
          {t('needsAttention.priceBreakdown')}
        </Typography>
        {pricing?.chargeLines.map((cl: any, idx: number) => {
          const lineItem = getLineItem(cl);
          return (
            <Box key={idx} className={priceContentClasses.priceItem}>
              <Box className={priceContentClasses.priceLabel}>
                <Typography>{lineItem.name}</Typography>
              </Box>
              <Box className={priceContentClasses.priceDetail}>
                <Typography>{lineItem.description}</Typography>
                <Typography>{lineItem.amount}</Typography>
              </Box>
            </Box>
          );
        })}
        {addOnLineItems && addOnLineItems.length > 0 && (
          <>
            {addOnLineItems.map((addOn: TripLegAddOnLineItem, idx: number) => (
              <Box
                key={`addon-${idx}`}
                className={priceContentClasses.priceItem}>
                <Box className={priceContentClasses.priceLabel}>
                  <Typography>{addOn.lineItemDescription}</Typography>
                </Box>
                <Box className={priceContentClasses.priceDetail}>
                  <Typography>{t('needsAttention.approved')}</Typography>
                  <Typography>
                    {financialUtils.formatPrice(addOn.lineItemAmount)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}
        <Divider />
        <Box className={priceContentClasses.priceTotal}>
          <Typography>{t('needsAttention.total')}</Typography>
          <Typography>
            {financialUtils.formatPrice(calculateTotal())}
          </Typography>
        </Box>
      </Box>
    </StyledBox>
  );
}
